import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';

const MAPBOX_URL = 'https://api.mapbox.com/mapbox-gl-js/v3.33.0/mapbox-gl';
const GEOCODE_URL = 'https://api.mapbox.com/search/geocode/v6/forward';
const MOBILE_QUERY = '(max-width: 749px)';
const NEAREST_LIMIT = 10;
const MAX_PAGES = 20;
const DEFAULT_VIEW = { center: [6, 51], zoom: 4 };
const CLUSTER_RADIUS = 40;
const UNCLUSTERED_ZOOM = 13;

const PIN_SVG =
  '<svg viewBox="0 0 26 36" aria-hidden="true" focusable="false"><path fill="currentColor" stroke-width="1.5" d="M13 1C6 1 1 6 1 13c0 8 12 22 12 22s12-14 12-22C25 6 20 1 13 1z"/></svg>';

/** @type {Promise<any> | null} */
let mapboxPromise = null;

/**
 * Resolves once the element comes within reach of the viewport, or when the signal aborts.
 * @param {Element} element
 * @param {AbortSignal} signal
 */
function nearViewport(element, signal) {
  return new Promise((resolve) => {
    if (typeof IntersectionObserver !== 'function') return resolve(undefined);
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        resolve(undefined);
      },
      { rootMargin: '800px 0px' }
    );
    observer.observe(element);
    signal.addEventListener(
      'abort',
      () => {
        observer.disconnect();
        resolve(undefined);
      },
      { once: true }
    );
  });
}

function loadMapbox() {
  mapboxPromise ??= new Promise((resolve, reject) => {
    const css = Object.assign(document.createElement('link'), {
      rel: 'stylesheet',
      href: `${MAPBOX_URL}.css`,
      integrity: 'sha384-S0a+eT2dLtsB1suB6ZJ/8IX+0d0Fvix9weJQ67L3E+jHOvn6SUKwYXVDyGdMuJtL',
      crossOrigin: '',
    });
    const script = Object.assign(document.createElement('script'), {
      src: `${MAPBOX_URL}.js`,
      integrity: 'sha384-jdGuG3GDIph+ONsjITkxWmlUE2RR0At6jtOVirn6L/6dYy3WcAVDaBNI3X96rUT0',
      crossOrigin: '',
    });
    let loaded = 0;
    const fail = () => {
      clearTimeout(timeout);
      css.remove();
      script.remove();
      reject(new Error('Map assets unavailable'));
    };
    const timeout = setTimeout(fail, 15000);
    css.onload = script.onload = () => {
      loaded += 1;
      if (loaded === 2) {
        clearTimeout(timeout);
        resolve(/** @type {any} */ (window).mapboxgl);
      }
    };
    css.onerror = script.onerror = fail;
    document.head.append(css, script);
  }).catch((error) => {
    mapboxPromise = null;
    throw error;
  });
  return mapboxPromise;
}

/** @param {unknown} value */
const normalize = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLocaleLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

/**
 * @param {unknown} latitude
 * @param {unknown} longitude
 * @returns {[number, number] | null}
 */
function coordinates(latitude, longitude) {
  if (latitude == null || longitude == null || String(latitude).trim() === '' || String(longitude).trim() === '') {
    return null;
  }
  const point = /** @type {[number, number]} */ ([Number(latitude), Number(longitude)]);
  return point.every(Number.isFinite) && Math.abs(point[0]) <= 90 && Math.abs(point[1]) <= 180 ? point : null;
}

/**
 * Straight-line distance in kilometres.
 * @param {[number, number]} from
 * @param {[number, number]} to
 */
function distance(from, to) {
  const radians = Math.PI / 180;
  const a =
    Math.sin(((to[0] - from[0]) * radians) / 2) ** 2 +
    Math.cos(from[0] * radians) * Math.cos(to[0] * radians) * Math.sin(((to[1] - from[1]) * radians) / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(Math.min(1, a)), Math.sqrt(Math.max(0, 1 - a)));
}

/** @param {[number, number]} point */
const lngLat = (point) => /** @type {[number, number]} */ ([point[1], point[0]]);

/**
 * @param {any} mapbox
 * @param {Store[]} stores
 */
function storeBounds(mapbox, stores) {
  const bounds = new mapbox.LngLatBounds();
  stores.forEach((store) => store.point && bounds.extend(lngLat(store.point)));
  return bounds;
}

/** @type {Map<string, string> | null} */
let regionCodes = null;

/**
 * Country names are stored in English; finds the region code behind one, e.g. "Netherlands" → "NL".
 * @param {string} name
 */
function regionCode(name) {
  if (!regionCodes) {
    regionCodes = new Map();
    const english = new Intl.DisplayNames(['en'], { type: 'region' });
    for (let first = 65; first <= 90; first += 1) {
      for (let second = 65; second <= 90; second += 1) {
        const code = String.fromCharCode(first, second);
        const label = english.of(code);
        if (label && label !== code) regionCodes.set(normalize(label), code);
      }
    }
  }
  return regionCodes.get(normalize(name));
}

/**
 * @param {Element | undefined} element
 * @param {boolean} hidden
 */
function setHidden(element, hidden) {
  if (!(element instanceof HTMLElement)) return;
  element.hidden = hidden;
  element.classList.toggle('hidden', hidden);
}

/**
 * @typedef {{ card: HTMLElement, name: string, search: string, nameSearch: string, city: string, point: [number, number] | null }} Store
 * @typedef {{ label: string, point?: [number, number] | null, store?: Store }} Suggestion
 */

/**
 * Searchable list of store locations with an optional Mapbox map.
 *
 * Mapbox is contacted only once the visitor has allowed preference cookies or asked for the map,
 * because loading it shares their IP address with Mapbox.
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} search
 * @property {HTMLUListElement} suggestions
 * @property {HTMLButtonElement} nearby
 * @property {HTMLButtonElement} clear
 * @property {HTMLButtonElement[]} [viewButtons]
 * @property {HTMLElement} [map]
 * @property {HTMLElement} [mapNotice]
 * @property {HTMLElement} mapStatus
 * @property {HTMLElement} geoStatus
 * @property {HTMLElement} status
 * @property {HTMLElement} results
 * @property {HTMLButtonElement} retry
 * @property {HTMLElement} empty
 * @property {HTMLUListElement} list
 * @property {HTMLElement} [pagination]
 * @property {HTMLScriptElement} strings
 *
 * @extends {Component<Refs>}
 */
class StoreLocator extends Component {
  requiredRefs = ['form', 'search', 'suggestions', 'status', 'list', 'strings'];

  /** @type {AbortController | null} */
  #lifecycle = null;
  /** @type {Record<string, string>} */
  #strings = {};
  /** @type {Store[]} */
  #stores = [];
  /** @type {[number, number] | null} */
  #origin = null;
  /** @type {string | null} */
  #originQuery = null;
  #request = 0;
  /** @type {AbortController | null} */
  #addressRequest = null;
  /** @type {AbortController | null} */
  #suggestionRequest = null;
  /** @type {Suggestion[]} */
  #suggestions = [];
  #activeSuggestion = -1;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #inputTimer;
  #loading = false;
  #mapboxAllowed = false;
  #mapLoading = false;
  /** @type {any} */
  #map = null;
  /** @type {Map<Store, any> | null} */
  #markers = null;
  /** @type {Map<string, { marker: any, stores: Store[] }>} */
  #clusters = new Map();
  /** @type {any} */
  #originMarker = null;
  /** @type {any} */
  #popup = null;
  /** @type {Store | null} */
  #popupStore = null;
  /** @type {HTMLElement | null} */
  #popupTrigger = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();
    if (this.#lifecycle) return;
    this.#lifecycle = new AbortController();
    const { signal } = this.#lifecycle;

    this.#strings = this.#readStrings();
    // Keeps focus in the search field so its blur doesn't empty the list before the click lands.
    for (const type of ['pointerdown', 'mousedown']) {
      this.refs.suggestions.addEventListener(type, (event) => event.preventDefault(), { signal });
    }
    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.#popup) this.#closePopup();
      },
      { signal }
    );

    if (this.#token) this.#watchConsent(signal);
    this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#lifecycle?.abort();
    this.#lifecycle = null;
    this.#addressRequest?.abort();
    this.closeSuggestions();
    clearTimeout(this.#inputTimer);
    this.#resizeObserver?.disconnect();
    this.#map?.remove();
    this.#map = null;
    this.#markers = null;
    this.#clusters = new Map();
    this.#originMarker = null;
    this.#popup = null;
    this.#mapLoading = false;
    this.#loading = false;
    this.#request += 1;
  }

  get #token() {
    const token = this.dataset.mapToken ?? '';
    return token.startsWith('pk.') ? token : '';
  }

  get #geocoding() {
    return Boolean(this.#token && this.#mapboxAllowed);
  }

  get #isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  get #mapOnly() {
    return this.hasAttribute('data-map-only');
  }

  get #listHidden() {
    return this.#mapOnly || (this.#isMobile && this.dataset.view === 'map');
  }

  /** Values from `| t` arrive HTML-escaped; decode them for textContent. */
  #readStrings() {
    const decoder = document.createElement('textarea');
    const raw = JSON.parse(this.refs.strings.textContent || '{}');
    return Object.fromEntries(
      Object.entries(raw).map(([key, value]) => {
        decoder.innerHTML = String(value);
        return [key, decoder.value];
      })
    );
  }

  /**
   * @param {string} key
   * @param {Record<string, string | number>} [values]
   */
  #t(key, values = {}) {
    return Object.entries(values).reduce(
      (text, [name, value]) => text.replaceAll(`[${name}]`, String(value)),
      this.#strings[key] ?? ''
    );
  }

  /**
   * @param {string} key
   * @param {number} count
   */
  #plural(key, count) {
    const form = new Intl.PluralRules(this.dataset.language).select(count) === 'one' ? 'one' : 'other';
    return this.#t(`${key}_${form}`, { number: count });
  }

  /** @param {AbortSignal} signal */
  #watchConsent(signal) {
    const shopify = /** @type {any} */ (window).Shopify;
    if (shopify?.designMode) {
      this.showMap();
      return;
    }
    if (typeof shopify?.loadFeatures !== 'function') return;

    const check = () => {
      if (shopify.customerPrivacy?.preferencesProcessingAllowed?.()) this.showMap();
    };
    shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (/** @type {unknown} */ error) => {
      if (error || signal.aborted) return;
      check();
      document.addEventListener('visitorConsentCollected', check, { signal });
    });
  }

  showMap() {
    if (this.#mapboxAllowed || !this.#token) return;
    this.#mapboxAllowed = true;
    const { map, mapNotice } = this.refs;
    if (map && mapNotice?.contains(document.activeElement)) {
      map.tabIndex = -1;
      map.focus({ preventScroll: true });
    }
    setHidden(mapNotice, true);
    if (this.#stores.length) this.#initializeMap();
  }

  retry() {
    this.#load();
  }

  async #load() {
    const lifecycle = this.#lifecycle;
    if (this.#loading || !lifecycle) return;
    this.#loading = true;
    const retryFocused = this.refs.retry.contains(document.activeElement);
    this.refs.retry.setAttribute('aria-disabled', 'true');

    try {
      const pages = Number(this.dataset.pages);
      const current = Number(this.dataset.page);
      if (!Number.isInteger(pages) || pages < 1 || pages > MAX_PAGES) throw new Error('Unsupported page count');
      if (pages > 1) this.refs.status.textContent = this.#t('loading');

      const cards = new Map(
        Array.from(this.refs.list.children, (card) => [/** @type {HTMLElement} */ (card).dataset.locationId, card])
      );
      for (let page = 1; page <= pages; page += 1) {
        if (page === current) continue;
        const url = new URL(window.location.href);
        url.hash = '';
        url.searchParams.set(this.dataset.pageParam ?? 'page', String(page));
        const html = await sectionRenderer.getSectionHTML(this.dataset.sectionId ?? '', false, url);
        if (lifecycle.signal.aborted) return;
        const other = new DOMParser().parseFromString(html, 'text/html').getElementById(this.id);
        if (!other || Number(other.dataset.page) !== page || Number(other.dataset.pages) !== pages) {
          throw new Error('Store pages changed');
        }
        other.querySelectorAll('[data-location-id]').forEach((card) => {
          cards.set(/** @type {HTMLElement} */ (card).dataset.locationId, document.importNode(card, true));
        });
      }
      if (cards.size !== Number(this.dataset.total)) throw new Error('Incomplete store list');

      const language = this.dataset.language ?? '';
      const regions =
        language && !language.startsWith('en') && typeof Intl.DisplayNames === 'function'
          ? new Intl.DisplayNames([language], { type: 'region' })
          : null;
      this.#stores = Array.from(cards.values(), (element) => {
        const card = /** @type {HTMLElement} */ (element);
        const name = card.dataset.name ?? '';
        const country = card.querySelector('[data-country]');
        const code = regions && country ? regionCode(country.textContent ?? '') : undefined;
        if (code && country) country.textContent = regions?.of(code) ?? country.textContent;
        return {
          card,
          name,
          city: normalize(card.dataset.city),
          search: normalize(`${card.dataset.search} ${country?.textContent ?? ''}`),
          nameSearch: normalize(name),
          point: coordinates(card.dataset.latitude, card.dataset.longitude),
        };
      });

      setHidden(this.refs.form, false);
      setHidden(this.refs.pagination, true);
      setHidden(this.refs.retry, true);
      if (retryFocused) this.refs.search.focus();
      setHidden(this.refs.nearby, !navigator.geolocation || !this.#stores.some((store) => store.point));
      this.#filter();
      if (this.#mapboxAllowed) this.#initializeMap();
    } catch {
      if (lifecycle.signal.aborted) return;
      (this.#mapOnly ? this.refs.mapStatus : this.refs.status).textContent = this.#t('load_failed');
      setHidden(this.refs.retry, false);
      setHidden(this.refs.pagination, false);
    } finally {
      if (this.#lifecycle === lifecycle) {
        this.#loading = false;
        this.refs.retry.removeAttribute('aria-disabled');
      }
    }
  }

  /**
   * @param {Store} store
   * @param {string} query
   * @param {'search' | 'nameSearch'} [field]
   */
  #matches(store, query, field = 'search') {
    return normalize(query)
      .split(' ')
      .every((word) => store[field].includes(word));
  }

  #filter() {
    if (!this.#stores.length) return;
    const { list, results } = this.refs;
    const focused = list.contains(document.activeElement) ? /** @type {HTMLElement} */ (document.activeElement) : null;
    const origin = this.#origin;
    const query = this.#originQuery === this.refs.search.value.trim() ? '' : this.refs.search.value;
    const km = (/** @type {Store} */ store) => (origin && store.point ? distance(origin, store.point) : Infinity);
    const ordered = [...this.#stores].sort((a, b) => km(a) - km(b) || a.name.localeCompare(b.name));
    const decimal = new Intl.NumberFormat(this.dataset.language || undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });

    let count = 0;
    ordered.forEach((store, index) => {
      const visible = this.#matches(store, query) && !(origin && count >= NEAREST_LIMIT);
      setHidden(store.card, !visible);
      if (visible) {
        count += 1;
      } else {
        store.card.removeAttribute('aria-current');
        store.card.querySelector('[aria-pressed]')?.setAttribute('aria-pressed', 'false');
      }

      const label = store.card.querySelector('[data-distance]');
      setHidden(label ?? undefined, !origin || !store.point);
      if (label) label.textContent = origin && store.point ? this.#t('distance', { number: decimal.format(km(store)) }) : '';
      if (list.children[index] !== store.card) list.insertBefore(store.card, list.children[index] ?? null);
    });

    setHidden(this.refs.clear, !this.refs.search.value && !origin);
    setHidden(this.refs.empty, count > 0);
    // Rewriting the live region with the same text makes some screen readers repeat it.
    const message = origin ? this.#plural('nearest', count) : this.#plural('count', count);
    if (this.refs.status.textContent.trim() !== message) this.refs.status.textContent = message;
    this.#updateMap();

    if (!focused) {
      results.scrollTop = 0;
    } else {
      const target = focused.closest('[hidden]') ? results : focused;
      if (document.activeElement !== target) target.focus({ preventScroll: true });
    }
  }

  /** @param {Event} event */
  onSubmit(event) {
    event.preventDefault();
    this.#searchAddress();
  }

  onInput() {
    this.#suggestionRequest?.abort();
    this.#activeSuggestion = -1;
    this.#syncActiveDescendant();
    this.#resetOrigin();
    clearTimeout(this.#inputTimer);
    this.#inputTimer = setTimeout(() => {
      this.#filter();
      if (this.refs.search.value.trim().length < 3) this.closeSuggestions();
      else this.#suggest();
    }, 300);
  }

  clear() {
    this.closeSuggestions();
    this.#resetOrigin();
    this.refs.search.value = '';
    this.#closePopup();
    this.#filter();
    this.refs.search.focus();
  }

  #resetOrigin() {
    this.#request += 1;
    this.#addressRequest?.abort();
    this.refs.nearby.removeAttribute('aria-disabled');
    this.refs.geoStatus.textContent = '';
    this.#origin = null;
    this.#originQuery = null;
  }

  /**
   * @param {[number, number] | null} point
   * @param {string} message
   * @param {string | null} [query]
   */
  #setOrigin(point, message, query = null) {
    this.#origin = point;
    this.#originQuery = point ? query : null;
    this.refs.geoStatus.textContent = message;
    this.#filter();
  }

  /** @param {URLSearchParams} params */
  #geocodeURL(params) {
    const url = new URL(GEOCODE_URL);
    params.forEach((value, key) => url.searchParams.set(key, value));
    if (this.dataset.countries) url.searchParams.set('country', this.dataset.countries);
    if (this.dataset.language) url.searchParams.set('language', this.dataset.language);
    url.searchParams.set('access_token', this.#token);
    return url;
  }

  /**
   * @param {URL} url
   * @param {AbortSignal} signal
   */
  async #fetchJSON(url, signal) {
    const timeout = AbortSignal.timeout(15000);
    const response = await fetch(url, { credentials: 'omit', signal: AbortSignal.any([signal, timeout]) });
    if (!response.ok) throw new Error('Request failed');
    return response.json();
  }

  /** @param {any} feature */
  #featurePoint(feature) {
    const [longitude, latitude] = feature?.geometry?.coordinates ?? [];
    return coordinates(latitude, longitude);
  }

  async #searchAddress() {
    this.closeSuggestions();
    const query = this.refs.search.value.trim();
    this.#resetOrigin();
    const request = this.#request;
    const isCity = this.#stores.some((store) => store.city === normalize(query));
    const isStore = !isCity && this.#stores.some((store) => this.#matches(store, query, 'nameSearch'));
    if (!query || !this.#geocoding || isStore) {
      this.#filter();
      return;
    }

    const controller = new AbortController();
    this.#addressRequest = controller;
    this.refs.geoStatus.textContent = this.#t('searching');
    try {
      const url = this.#geocodeURL(new URLSearchParams({ q: query, limit: '1', autocomplete: 'false' }));
      const result = await this.#fetchJSON(url, controller.signal);
      if (request !== this.#request) return;
      const point = this.#featurePoint(result.features?.[0]);
      this.#setOrigin(point, point ? this.#t('near_query', { query }) : this.#t('address_not_found'), query);
    } catch {
      if (request !== this.#request) return;
      this.#setOrigin(null, this.#t('address_failed'));
    }
  }

  async #suggest() {
    const { search } = this.refs;
    const query = search.value.trim();
    if (query.length < 3 || document.activeElement !== search) return;

    /** @type {Suggestion[]} */
    const local = this.#stores
      .filter((store) => this.#matches(store, query, 'nameSearch'))
      .slice(0, 3)
      .map((store) => ({ label: store.name, store }));
    this.#renderSuggestions(local);
    if (!this.#geocoding) return;

    const controller = new AbortController();
    this.#suggestionRequest = controller;
    try {
      const url = this.#geocodeURL(new URLSearchParams({ q: query, limit: '5', autocomplete: 'true' }));
      const result = await this.#fetchJSON(url, controller.signal);
      if (this.#suggestionRequest !== controller || document.activeElement !== search || search.value.trim() !== query) {
        return;
      }
      /** @type {Suggestion[]} */
      const places = (result.features ?? []).flatMap((/** @type {any} */ feature) => {
        const point = this.#featurePoint(feature);
        const properties = feature.properties ?? {};
        const label =
          properties.full_address || [properties.name, properties.place_formatted].filter(Boolean).join(', ');
        return point && label ? [{ label, point }] : [];
      });
      this.#renderSuggestions([...places, ...local]);
    } catch {
      // Store-name suggestions stay available when address suggestions fail.
    }
  }

  /** @param {Suggestion[]} suggestions */
  #renderSuggestions(suggestions) {
    const { search, suggestions: listbox } = this.refs;
    const selected = this.#suggestions[this.#activeSuggestion];
    this.#suggestions = suggestions;
    this.#activeSuggestion = selected
      ? suggestions.findIndex((item) => item.label === selected.label && item.store === selected.store)
      : -1;

    listbox.replaceChildren(
      ...suggestions.map((suggestion, index) => {
        const option = document.createElement('li');
        option.id = `${this.id}-suggestion-${index}`;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', String(index === this.#activeSuggestion));
        option.setAttribute('on:click', `/chooseSuggestion/${index}`);
        option.textContent = suggestion.label;
        return option;
      })
    );
    setHidden(listbox, suggestions.length === 0);
    search.setAttribute('aria-expanded', String(suggestions.length > 0));
    this.#syncActiveDescendant();
  }

  #syncActiveDescendant() {
    const { search, suggestions } = this.refs;
    const active = suggestions.children[this.#activeSuggestion];
    [...suggestions.children].forEach((option) =>
      option.setAttribute('aria-selected', String(option === active))
    );
    if (active) {
      search.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    } else {
      search.removeAttribute('aria-activedescendant');
    }
  }

  closeSuggestions() {
    this.#suggestionRequest?.abort();
    this.#suggestionRequest = null;
    this.#suggestions = [];
    this.#activeSuggestion = -1;
    if (!this.refs.suggestions) return;
    this.refs.suggestions.replaceChildren();
    setHidden(this.refs.suggestions, true);
    this.refs.search.setAttribute('aria-expanded', 'false');
    this.refs.search.removeAttribute('aria-activedescendant');
  }

  /** @param {KeyboardEvent} event */
  onSearchKeydown(event) {
    if (event.isComposing) return;
    const count = this.#suggestions.length;

    if (event.key === 'Escape' && count) {
      event.preventDefault();
      event.stopPropagation();
      this.closeSuggestions();
    } else if (count && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.#activeSuggestion =
        this.#activeSuggestion < 0 ? (step > 0 ? 0 : count - 1) : (this.#activeSuggestion + step + count) % count;
      this.#syncActiveDescendant();
    } else if (event.key === 'Enter' && this.#activeSuggestion >= 0) {
      event.preventDefault();
      this.chooseSuggestion(this.#activeSuggestion);
    }
  }

  /** @param {number} index */
  chooseSuggestion(index) {
    const suggestion = this.#suggestions[index];
    if (!suggestion) return;
    this.closeSuggestions();
    this.#resetOrigin();
    this.refs.search.value = suggestion.label;
    const point = suggestion.point ?? null;
    this.#setOrigin(point, point ? this.#t('near_query', { query: suggestion.label }) : '', suggestion.label);
    if (suggestion.store) this.#select(suggestion.store, true);
  }

  locate() {
    const { nearby, search } = this.refs;
    if (nearby.getAttribute('aria-disabled') === 'true') return;
    this.closeSuggestions();
    this.#resetOrigin();
    const request = this.#request;
    nearby.setAttribute('aria-disabled', 'true');
    this.refs.geoStatus.textContent = this.#t('locating');

    const fail = () => {
      clearTimeout(stall);
      if (request !== this.#request) return;
      nearby.removeAttribute('aria-disabled');
      this.refs.geoStatus.textContent = this.#t('location_denied');
    };
    // Some in-app browsers never show the permission prompt or call back, and the API's own
    // timeout only starts once permission is granted.
    const stall = setTimeout(fail, 20000);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(stall);
        if (request !== this.#request) return;
        nearby.removeAttribute('aria-disabled');
        search.value = '';
        const point = coordinates(position.coords.latitude, position.coords.longitude);
        this.#setOrigin(point, point ? this.#t('near_you') : this.#t('location_denied'));
      },
      fail,
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }

  /** @param {Event} event */
  onSelect(event) {
    const card = /** @type {HTMLElement} */ (event.target).closest('[data-location-id]');
    const store = this.#stores.find((item) => item.card === card);
    if (store) this.#select(store, true);
  }

  /** @param {'list' | 'map'} view */
  setView(view) {
    this.dataset.view = view;
    this.refs.viewButtons?.forEach((button, index) =>
      button.setAttribute('aria-pressed', String((index === 0 ? 'list' : 'map') === view))
    );
    if (view === 'map' && this.#map) {
      this.#map.resize();
      this.#updateMap();
    }
  }

  async #initializeMap() {
    const lifecycle = this.#lifecycle;
    const container = this.refs.map;
    if (!container || !lifecycle || this.#map || this.#mapLoading) return;
    this.#mapLoading = true;

    try {
      // A map far down the page, such as on the home page, waits for the visitor to scroll near it.
      await nearViewport(this, lifecycle.signal);
      if (lifecycle.signal.aborted) return;
      this.refs.mapStatus.textContent = this.#t('map_loading');
      const mapbox = await loadMapbox();
      if (lifecycle.signal.aborted) return;
      if (!mapbox.supported()) throw new Error('WebGL unavailable');

      container.replaceChildren();
      const map = new mapbox.Map({
        container,
        accessToken: this.#token,
        style: 'mapbox://styles/mapbox/streets-v12',
        language: this.dataset.language,
        locale: {
          'Map.Title': this.#t('map_canvas'),
          'NavigationControl.ZoomIn': this.#t('zoom_in'),
          'NavigationControl.ZoomOut': this.#t('zoom_out'),
          'ScrollZoomBlocker.CtrlMessage': this.#t('scroll_zoom_ctrl'),
          'ScrollZoomBlocker.CmdMessage': this.#t('scroll_zoom_cmd'),
          'TouchPanBlocker.Message': this.#t('touch_pan'),
          'AttributionControl.ToggleAttribution': this.#t('attribution'),
          'LogoControl.Title': this.#t('mapbox_home'),
        },
        ...DEFAULT_VIEW,
        // The style's globe switches in after load and bends distances as the map pans, so pin groups computed
        // per zoom would drift into each other; a flat map keeps them apart.
        projection: 'mercator',
        maxZoom: 19,
        cooperativeGestures: true,
        dragRotate: false,
        pitchWithRotate: false,
        touchPitch: false,
      });
      this.#map = map;
      map.touchZoomRotate.disableRotation();
      map.keyboard.disableRotation();
      map.getCanvas().classList.add('focus-inset');
      if (document.activeElement === container) map.getCanvas().focus({ preventScroll: true });
      container.removeAttribute('tabindex');
      map.addControl(new mapbox.NavigationControl({ showCompass: false }), 'bottom-right');
      map.on('zoomend', () => this.#cluster());
      map.on('error', () => {
        this.refs.mapStatus.textContent = this.#t('map_failed');
      });
      container.addEventListener(
        'click',
        (event) => {
          if (/** @type {Element} */ (event.target).closest('.mapboxgl-popup-close-button')) this.#closePopup();
        },
        { capture: true, signal: lifecycle.signal }
      );

      this.#markers = new Map();
      for (const store of this.#stores) {
        if (!store.point) continue;
        const point = store.point;
        const pin = document.createElement('button');
        pin.type = 'button';
        pin.className = 'button-unstyled store-locator__pin';
        pin.setAttribute('aria-label', store.name);
        pin.setAttribute('aria-pressed', 'false');
        pin.innerHTML = PIN_SVG;
        pin.addEventListener('click', () => this.#select(store, false), { signal: lifecycle.signal });
        pin.addEventListener(
          'focus',
          () => {
            if (!map.getBounds().contains(lngLat(point))) map.jumpTo({ center: lngLat(point) });
          },
          { signal: lifecycle.signal }
        );
        const marker = new mapbox.Marker({ element: pin, anchor: 'bottom' }).setLngLat(lngLat(point));
        // Mapbox sets role="img" on a marker element that has no role, which hides the button.
        pin.removeAttribute('role');
        this.#markers.set(store, marker);
      }

      this.#resizeObserver = new ResizeObserver(() => this.#map?.resize());
      this.#resizeObserver.observe(container);
      this.#markers.forEach((_, store) => {
        const select = document.createElement('button');
        select.type = 'button';
        select.className = 'button-unstyled store-locator__select';
        select.textContent = store.name;
        select.setAttribute('aria-pressed', String(store.card.getAttribute('aria-current') === 'true'));
        select.setAttribute('on:click', '/onSelect');
        store.card.querySelector('[data-store-name]')?.replaceChildren(select);
      });
      this.refs.mapStatus.textContent = '';
      this.#updateMap();
    } catch {
      if (lifecycle.signal.aborted) return;
      const hadFocus = container.contains(document.activeElement);
      this.#map?.remove();
      this.#map = null;
      this.#markers = null;
      this.#clusters = new Map();
      this.#resizeObserver?.disconnect();
      this.refs.mapStatus.textContent = this.#t('map_failed');
      container.remove();
      if (hadFocus) this.refs.results.focus({ preventScroll: true });
      this.querySelector('.store-locator__views')?.remove();
      delete this.dataset.map;
    } finally {
      if (this.#lifecycle === lifecycle) this.#mapLoading = false;
    }
  }

  #updateMap() {
    const map = this.#map;
    const mapbox = /** @type {any} */ (window).mapboxgl;
    if (!map || !this.#markers || !mapbox) return;

    /** @type {[number, number][]} */
    const points = [];
    this.#markers.forEach((marker, store) => {
      if (store.card.hidden) {
        if (store === this.#popupStore) this.#closePopup();
        if (marker.getElement() === document.activeElement) map.getCanvas().focus({ preventScroll: true });
        marker.remove();
        marker.getElement().setAttribute('aria-pressed', 'false');
      } else if (store.point) {
        points.push(store.point);
      }
    });

    this.#originMarker?.remove();
    this.#originMarker = null;
    if (this.#origin) {
      const color = getComputedStyle(this).getPropertyValue('--color-foreground').trim();
      this.#originMarker = new mapbox.Marker(color ? { color } : {}).setLngLat(lngLat(this.#origin)).addTo(map);
      const element = this.#originMarker.getElement();
      element.setAttribute('role', 'img');
      element.setAttribute('aria-label', this.#originQuery ?? this.#t('your_location'));
      points.push(this.#origin);
    }

    if (points.length) {
      const bounds = new mapbox.LngLatBounds();
      points.forEach((point) => bounds.extend(lngLat(point)));
      map.fitBounds(bounds, { padding: 32, maxZoom: UNCLUSTERED_ZOOM, duration: 0 });
    } else {
      map.jumpTo(DEFAULT_VIEW);
    }
    this.#cluster();
  }

  /** Merges pins that would overlap at the current zoom into one button that zooms in on them. */
  #cluster() {
    const map = this.#map;
    const mapbox = /** @type {any} */ (window).mapboxgl;
    const signal = this.#lifecycle?.signal;
    if (!map || !this.#markers || !mapbox || !signal) return;
    const focused = this.#focusedStores();

    /** @type {{ x: number, y: number, stores: Store[] }[]} */
    const groups = [];
    const clustering = map.getZoom() < UNCLUSTERED_ZOOM;
    this.#markers.forEach((_, store) => {
      if (store.card.hidden || !store.point) return;
      const { x, y } = map.project(lngLat(store.point));
      const group = clustering
        ? groups.find((item) => Math.hypot(item.x - x, item.y - y) < CLUSTER_RADIUS)
        : undefined;
      if (group) group.stores.push(store);
      else groups.push({ x, y, stores: [store] });
    });

    // A group's button sits in the middle of its stores, which can land it on a neighbour: merge until none overlap.
    for (let index = 0; clustering && index < groups.length; index += 1) {
      const group = groups[index];
      if (group.stores.length > 1) {
        const { x, y } = map.project(storeBounds(mapbox, group.stores).getCenter());
        Object.assign(group, { x, y });
      }
      const other = groups.findIndex(
        (item, at) => at < index && Math.hypot(item.x - group.x, item.y - group.y) < CLUSTER_RADIUS
      );
      if (other === -1) continue;
      groups[other].stores.push(...group.stores);
      groups.splice(index, 1);
      index = other - 1;
    }

    /** @type {Map<string, { marker: any, stores: Store[] }>} */
    const clusters = new Map();
    for (const { stores } of groups) {
      if (stores.length === 1) {
        const marker = this.#markers.get(stores[0]);
        if (!marker.getElement().isConnected) marker.addTo(map);
        continue;
      }
      for (const store of stores) {
        if (store === this.#popupStore) this.#closePopup();
        this.#markers.get(store)?.remove();
      }
      const key = stores.map((store) => store.card.dataset.locationId).join();
      clusters.set(key, this.#clusters.get(key) ?? this.#createCluster(stores, map, mapbox, signal));
    }
    this.#clusters.forEach((cluster, key) => {
      if (!clusters.has(key)) cluster.marker.remove();
    });
    clusters.forEach((cluster) => {
      if (!cluster.marker.getElement().isConnected) cluster.marker.addTo(map);
    });
    this.#clusters = clusters;

    // Focus follows a pin into the group it joined, and a group's first store when it splits.
    if (focused.length && !this.refs.map?.contains(document.activeElement)) {
      const markers = this.#markers;
      const target = focused
        .map((store) => {
          const pin = markers.get(store)?.getElement();
          if (pin?.isConnected) return pin;
          return [...clusters.values()].find((cluster) => cluster.stores.includes(store))?.marker.getElement();
        })
        .find(Boolean);
      (target ?? map.getCanvas()).focus({ preventScroll: true });
    }
  }

  /** @returns {Store[]} */
  #focusedStores() {
    const active = document.activeElement;
    for (const cluster of this.#clusters.values()) {
      if (cluster.marker.getElement() === active) return cluster.stores;
    }
    for (const [store, marker] of this.#markers ?? []) {
      if (marker.getElement() === active) return [store];
    }
    return [];
  }

  /**
   * @param {Store[]} stores
   * @param {any} map
   * @param {any} mapbox
   * @param {AbortSignal} signal
   */
  #createCluster(stores, map, mapbox, signal) {
    const bounds = storeBounds(mapbox, stores);
    const center = bounds.getCenter();

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button-unstyled store-locator__cluster';
    button.textContent = String(stores.length);
    button.setAttribute('aria-label', this.#t('cluster', { number: stores.length }));
    button.addEventListener(
      'click',
      () => {
        map.fitBounds(bounds, { padding: 64, maxZoom: UNCLUSTERED_ZOOM, duration: 0 });
        this.#cluster();
      },
      { signal }
    );
    button.addEventListener(
      'focus',
      () => {
        if (!map.getBounds().contains(center)) map.jumpTo({ center });
      },
      { signal }
    );
    const marker = new mapbox.Marker({ element: button }).setLngLat(center);
    // Mapbox sets role="img" on a marker element that has no role, which hides the button.
    button.removeAttribute('role');
    return { marker, stores };
  }

  /**
   * @param {Store} store
   * @param {boolean} moveMap - true when chosen from the list, false when chosen on the map
   */
  #select(store, moveMap) {
    this.#stores.forEach((item) => {
      item.card.toggleAttribute('aria-current', false);
      item.card.querySelector('[aria-pressed]')?.setAttribute('aria-pressed', String(item === store));
    });
    this.#closePopup();
    this.#markers?.forEach((marker, item) =>
      marker.getElement().setAttribute('aria-pressed', String(item === store))
    );
    store.card.setAttribute('aria-current', 'true');
    const trigger = /** @type {HTMLElement | null} */ (store.card.querySelector('[aria-pressed]'));

    const marker = this.#markers?.get(store);
    const map = this.#map;
    if (marker && map && store.point) {
      const showOnMap = moveMap && this.#isMobile;
      if (showOnMap) this.setView('map');
      if (moveMap) map.jumpTo({ center: lngLat(store.point), zoom: Math.max(UNCLUSTERED_ZOOM, map.getZoom()) });

      const content = document.createElement('div');
      const title = document.createElement('strong');
      title.id = `${this.id}-popup-title`;
      title.textContent = store.name;
      const address = document.createElement('p');
      const lines = store.card.querySelector('[data-address]')?.childNodes ?? [];
      address.append(...Array.from(lines, (node) => node.cloneNode(true)));
      const directions = document.createElement('p');
      const link = store.card.querySelector('[data-directions]');
      if (link) directions.append(link.cloneNode(true));
      content.append(title, address, directions);

      this.#popupStore = store;
      this.#popup = new /** @type {any} */ (window).mapboxgl.Popup({
        maxWidth: '15rem',
        focusAfterOpen: false,
        closeOnClick: false,
        offset: 36,
      })
        .setLngLat(lngLat(store.point))
        .setDOMContent(content)
        .addTo(map);
      this.#popup
        .getElement()
        .querySelector('.mapboxgl-popup-close-button')
        ?.setAttribute('aria-label', this.#t('close'));

      // With the list out of view, focus has nowhere else to go, and Mapbox puts the popup after every pin.
      if (this.#listHidden) {
        content.setAttribute('role', 'group');
        content.setAttribute('aria-labelledby', title.id);
        content.tabIndex = -1;
        content.focus({ preventScroll: true });
      }
    }

    this.#popupTrigger = this.#listHidden ? null : trigger;
    if (!moveMap && !this.#listHidden) {
      store.card.scrollIntoView({ block: 'nearest' });
      trigger?.focus({ preventScroll: true });
    }
  }

  #closePopup() {
    const popup = this.#popup;
    const store = this.#popupStore;
    const restoreFocus = popup?.getElement().contains(document.activeElement);
    popup?.remove();
    this.#popup = null;
    this.#popupStore = null;
    if (restoreFocus) {
      const pin = store ? this.#markers?.get(store)?.getElement() : null;
      const target = [this.#popupTrigger, pin].find((element) => element?.isConnected && element.getClientRects().length);
      (target ?? this.#map?.getCanvas())?.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('store-locator-component')) {
  customElements.define('store-locator-component', StoreLocator);
}
